import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, ja } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCalendarMonths } from '@hooks/useCalendarMonths';
import { cn } from '@/lib/utils';

const LOCALES = { ja, en: enUS } as const;

export interface DateRangePickerProps {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  id?: string;
  placeholder?: string;
  fromDate?: Date;
  toDate?: Date;
  disabled?: boolean;
  /** Months shown side by side. Two is shadcn's default for a range. */
  numberOfMonths?: number;
  className?: string;
}

/**
 * Popover + Calendar in range mode, per shadcn's date-range-picker recipe —
 * there is no single component for it. Replaces a pair of single pickers, which
 * could express a backwards range and gave the calendar no way to show the span.
 */
const DateRangePicker = React.forwardRef<HTMLButtonElement, DateRangePickerProps>(
  (
    { value, onChange, id, placeholder, fromDate, toDate, disabled, numberOfMonths = 2, className },
    ref,
  ) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const locale = LOCALES[i18n?.language?.slice(0, 2) as keyof typeof LOCALES] ?? enUS;
    const months = useCalendarMonths(numberOfMonths);

    const label = React.useMemo(() => {
      if (!value?.from) {
        return value?.to ? `– ${format(value.to, 'PP', { locale })}` : placeholder ?? t('common.pickDateRange');
      }
      const from = format(value.from, 'PP', { locale });
      return value.to ? `${from} – ${format(value.to, 'PP', { locale })}` : from;
    }, [value, locale, placeholder, t]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        {/* The clear control is a sibling of the trigger, not a child: a button
            cannot contain another interactive control, and as a real button it
            is reachable and operable by keyboard. */}
        <div className="relative">
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={id}
              type="button"
              variant="outline"
              disabled={disabled}
              data-empty={!value?.from && !value?.to}
              className={cn(
                'w-full justify-start gap-2 font-normal data-[empty=true]:text-muted-foreground',
                (value?.from || value?.to) && 'pr-9',
                className,
              )}
            >
              <CalendarIcon className="shrink-0" strokeWidth={1.8} />
              <span className="truncate">{label}</span>
            </Button>
          </PopoverTrigger>
          {(value?.from || value?.to) && !disabled && (
            <button
              type="button"
              aria-label={t('common.clear')}
              onClick={() => onChange(undefined)}
              className="absolute right-2 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3" strokeWidth={2.4} />
            </button>
          )}
        </div>

        <PopoverContent align="start" className="revy-ui w-auto p-0">
          <Calendar
            mode="range"
            selected={value}
            onSelect={onChange}
            defaultMonth={value?.from ?? value?.to}
            numberOfMonths={months}
            locale={locale}
            disabled={
              [...(fromDate ? [{ before: fromDate }] : []), ...(toDate ? [{ after: toDate }] : [])]
            }
            autoFocus
          />
        </PopoverContent>
      </Popover>
    );
  },
);
DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
